import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Wrench, Clock, Plus, CheckCircle, AlertCircle, ChevronRight, ChevronDown,
  Droplet, Circle, Octagon, Wind, Battery, Fan, Calendar, DollarSign, MapPin
} from 'lucide-react';

// Icon mapping
const ICON_MAP = {
  'droplet': Droplet,
  'circle': Circle,
  'octagon': Octagon,
  'wind': Wind,
  'battery': Battery,
  'fan': Fan,
  'wrench': Wrench,
};

// Status configurations
const STATUS_CONFIG = {
  overdue: { label: 'Overdue', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', icon: AlertCircle, barColor: 'bg-red-500' },
  due_soon: { label: 'Due Soon', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', icon: Clock, barColor: 'bg-amber-500' },
  ok: { label: 'On Track', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle, barColor: 'bg-green-500' },
  not_set: { label: 'Ready to Set Up', bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', icon: Clock, barColor: 'bg-slate-300' },
};

const MaintenanceTabNew = ({ 
  healthData, 
  vehicleId,
  onItemClick, 
  onLogService,
  onRefresh,
  theme 
}) => {
  const [showAllHistory, setShowAllHistory] = useState(false);
  const maintenanceItems = healthData?.maintenance_items || [];
  const serviceHistory = healthData?.service_history || [];
  
  const displayedHistory = showAllHistory ? serviceHistory : serviceHistory.slice(0, 5);

  // Calculate progress percentage for visual indicator
  const calculateProgress = (item) => {
    if (item.status === 'not_set') return 0;
    if (item.status === 'overdue') return 100;
    
    // Use days remaining if available
    if (item.days_remaining !== null && item.interval_months) {
      const totalDays = item.interval_months * 30;
      const elapsed = totalDays - item.days_remaining;
      return Math.min(100, Math.max(0, (elapsed / totalDays) * 100));
    }
    
    // Use miles remaining if available
    if (item.miles_remaining !== null && item.interval_miles) {
      const elapsed = item.interval_miles - item.miles_remaining;
      return Math.min(100, Math.max(0, (elapsed / item.interval_miles) * 100));
    }
    
    return item.status === 'due_soon' ? 75 : 50;
  };

  return (
    <div className="space-y-4">
      {/* Maintenance Items */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-800">Maintenance Tracking</h3>
          <Button
            size="sm"
            onClick={() => onLogService()}
            style={{ backgroundColor: theme.icon }}
            className="hover:opacity-90"
          >
            <Plus className="h-4 w-4 mr-1" />
            Log Service
          </Button>
        </div>
        
        <div className="space-y-2">
          {maintenanceItems.map((item) => {
            const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.not_set;
            const StatusIcon = statusConfig.icon;
            const ItemIcon = ICON_MAP[item.icon] || Wrench;
            const progressPercent = calculateProgress(item);
            
            return (
              <Card
                key={item.type}
                className={`border transition-all cursor-pointer hover:shadow-md ${statusConfig.border}`}
                style={{ backgroundColor: theme.surface }}
                onClick={() => onItemClick(item)}
                data-testid={`maintenance-item-${item.type}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${statusConfig.bg}`}>
                      <ItemIcon className={`h-6 w-6 ${statusConfig.text}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-800">{item.name}</h4>
                        <Badge className={`${statusConfig.bg} ${statusConfig.text} text-xs`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      
                      {/* Urgency Text or Details Row */}
                      {item.status !== 'not_set' ? (
                        <div className="space-y-1">
                          {/* Urgency Text */}
                          {item.urgency_text && (
                            <p className={`text-sm font-medium ${item.status === 'overdue' ? 'text-red-600' : 'text-amber-600'}`}>
                              {item.urgency_text}
                            </p>
                          )}
                          
                          {/* Next Due Info */}
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                            {item.time_remaining?.text && !item.urgency_text && (
                              <span>Due: {item.time_remaining.text}</span>
                            )}
                            {item.mileage_remaining?.text && !item.urgency_text && (
                              <span>{item.mileage_remaining.text}</span>
                            )}
                            <span className="text-slate-300">|</span>
                            <span className="text-slate-400">
                              Last: {item.last_service_date || 'N/A'}
                              {item.last_service_mileage && ` @ ${item.last_service_mileage.toLocaleString()} mi`}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400 italic">Tap to start tracking</span>
                      )}

                      {/* Progress Bar */}
                      {item.status !== 'not_set' && (
                        <div className="mt-2 w-full bg-slate-200 rounded-full h-1.5">
                          <div 
                            className={`h-1.5 rounded-full transition-all ${statusConfig.barColor}`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                      )}

                      {/* Interval Info */}
                      {item.status !== 'not_set' && (
                        <div className="text-xs text-slate-400 mt-1">
                          Interval: {item.interval_months ? `${item.interval_months} mo` : ''}
                          {item.interval_miles && item.interval_months ? ' / ' : ''}
                          {item.interval_miles ? `${item.interval_miles.toLocaleString()} mi` : ''}
                        </div>
                      )}
                    </div>

                    {/* Log Service Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLogService(item);
                      }}
                    >
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Service History Timeline */}
      <Card style={{ backgroundColor: theme.surface }} data-testid="service-history">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Calendar className="h-5 w-5" style={{ color: theme.icon }} />
            Service History
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {serviceHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Wrench className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p className="font-medium text-slate-500">Ready to Log Services</p>
              <p className="text-sm">Record your first service to start building your history</p>
            </div>
          ) : (
            <>
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />
                
                {displayedHistory.map((record, index) => (
                  <div key={record.id || index} className="relative flex gap-4 pb-4 last:pb-0">
                    {/* Timeline dot */}
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center z-10 flex-shrink-0"
                      style={{ backgroundColor: theme.light }}
                    >
                      <Wrench className="h-5 w-5" style={{ color: theme.icon }} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 bg-white rounded-lg border border-slate-100 p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-slate-800">
                            {record.service_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </h4>
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {record.date}
                            </span>
                            {record.mileage && (
                              <span>• {record.mileage.toLocaleString()} mi</span>
                            )}
                          </div>
                        </div>
                        {record.cost && (
                          <span className="font-medium text-slate-700">
                            ${record.cost.toLocaleString()}
                          </span>
                        )}
                      </div>
                      
                      {record.vendor_name && (
                        <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                          <MapPin className="h-3 w-3" />
                          {record.vendor_name}
                        </div>
                      )}
                      
                      {record.notes && (
                        <p className="mt-2 text-sm text-slate-500">{record.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {serviceHistory.length > 5 && (
                <Button
                  variant="ghost"
                  className="w-full mt-3"
                  onClick={() => setShowAllHistory(!showAllHistory)}
                >
                  {showAllHistory ? (
                    <>Show Less <ChevronDown className="h-4 w-4 ml-1 rotate-180" /></>
                  ) : (
                    <>Show All ({serviceHistory.length}) <ChevronDown className="h-4 w-4 ml-1" /></>
                  )}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MaintenanceTabNew;
